import { FC, useState } from "react";
import { TextField, useForm } from "@refinedev/antd";
import { AppViewCard } from "../../../../../components";
import { errorNotification, successNotification } from "../../../../../helpers";
import { CloseOutlined, EditOutlined } from "@ant-design/icons";
import { Button, Checkbox, Form, Input, Space } from "antd";
import s from "./Clubs-Change-Map-Coordinates.module.scss";

type ClubsChangeMapCoordinatesProps = {
  leagueId: number;
  teamId: number;
  mapX?: number | null;
  mapY?: number | null;
  hideMap?: boolean | null;
  onRefetchCard: () => void;
};

type MapCoordinatesFormValues = {
  mapX: number | string;
  mapY: number | string;
  hideMap: boolean;
};

const isCoordinateDefined = (value?: number | null) =>
  value !== null && value !== undefined;

const coordinateRule = (axis: "X" | "Y") => [
  {
    required: true,
    message: `Необходимо указать координату ${axis}`,
  },
  {
    validator: (_: unknown, value: number | string | undefined) => {
      if (value === null || value === undefined || value === "") {
        return Promise.resolve();
      }

      return Number.isFinite(Number(value))
        ? Promise.resolve()
        : Promise.reject(new Error("Укажите корректное число"));
    },
  },
];

export const ClubsChangeMapCoordinates: FC<ClubsChangeMapCoordinatesProps> = ({
  teamId,
  leagueId,
  mapX,
  mapY,
  hideMap,
  onRefetchCard,
}) => {
  const [edit, setEdit] = useState(false);

  const hasCoordinates = isCoordinateDefined(mapX) && isCoordinateDefined(mapY);

  const initialValues: MapCoordinatesFormValues = {
    mapX: mapX ?? "",
    mapY: mapY ?? "",
    hideMap: Boolean(hideMap),
  };

  const { formProps, form } = useForm<MapCoordinatesFormValues>({
    action: hasCoordinates ? "edit" : "create",
    resource: "clubs",
    meta: {
      teamId,
      leagueId,
    },
    dataProviderName: "clubsMapCoordinatesDataProvider",
    redirect: false,
    ...successNotification({
      successData: {
        message: hasCoordinates
          ? "Координаты изменены"
          : "Координаты добавлены",
        type: "success",
      },
      callback: () => {
        setEdit(false);
        onRefetchCard();
      },
    }),
    ...errorNotification({}),
  });

  const handleEdit = () => {
    setEdit(true);
  };

  const handleStopEdit = () => {
    setEdit(false);
    form.setFieldsValue(initialValues);
  };

  if (edit || !hasCoordinates) {
    return (
      <AppViewCard label={"Координаты на карте:"}>
        <Form
          style={{
            width: "100%",
          }}
          {...formProps}
          initialValues={initialValues}
          layout={"vertical"}
        >
          <div className={s.formRow}>
            <Form.Item
              name={"mapX"}
              label={"X"}
              style={{ flex: 1, marginBottom: 0 }}
              rules={coordinateRule("X")}
            >
              <Input type={"number"} step={"any"} placeholder={"Укажите X"} />
            </Form.Item>
            <Form.Item
              name={"mapY"}
              label={"Y"}
              style={{ flex: 1, marginBottom: 0 }}
              rules={coordinateRule("Y")}
            >
              <Input type={"number"} step={"any"} placeholder={"Укажите Y"} />
            </Form.Item>
            <Button type={"primary"} htmlType={"submit"}>
              {hasCoordinates ? "Изменить координаты" : "Добавить координаты"}
            </Button>
            {hasCoordinates && (
              <div className={s.iconContainer}>
                <CloseOutlined
                  style={{
                    fontSize: 20,
                    cursor: "pointer",
                    color: "var(--accent-main)",
                  }}
                  onClick={handleStopEdit}
                />
              </div>
            )}
          </div>
          <Form.Item
            name={"hideMap"}
            valuePropName={"checked"}
            style={{ marginBottom: 0, marginTop: 12 }}
          >
            <Checkbox>Не отображать на карте ФНЛ</Checkbox>
          </Form.Item>
        </Form>
      </AppViewCard>
    );
  }

  return (
    <AppViewCard label={"Координаты на карте:"}>
      <Space direction={"vertical"} style={{ width: "100%" }} size={12}>
        <Space
          direction={"horizontal"}
          style={{
            width: "100%",
            display: "flex",
            justifyContent: "space-between",
          }}
          size={24}
        >
          <TextField value={`X: ${mapX}; Y: ${mapY}`} />
          <div className={s.wrapContainer}>
            <EditOutlined
              style={{
                fontSize: 20,
                cursor: "pointer",
                color: "var(--accent-main)",
              }}
              onClick={handleEdit}
            />
          </div>
        </Space>
        <Checkbox checked={Boolean(hideMap)} disabled>
          Не отображать на карте ФНЛ
        </Checkbox>
      </Space>
    </AppViewCard>
  );
};
